import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, MenuItem, Stack, TextField, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import ActionButton from "../../../component/common/ActionButton";
import AttachmentPreviewList from "../../../component/common/AttachmentPreviewList";
import FormModal from "../../../component/common/FormModal";
import FormField from "../../../component/common/FormField";
import LoadingDots from "../../../component/common/LoadingDots";
import PendingFileList from "./PendingFileList";
import { workRequestApi } from "../api/workRequestApi";
import { runApiAction } from "../../../util/apiFeedback";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { formatDottedDate } from "../../../util/dateUtils";
import { ds } from "../../../styles/designSystem";

const C = ds.colors;
const EMPTY_FORM = { id: null, deptId: "", title: "", description: "", files: [], attachments: [] };

/**
 * 업무 메뉴얼 탭 — 팀별 카드 목록.
 *
 * <p><b>카드를 누르면 수정 모달이 열린다.</b> 카드마다 '파일 추가·수정·삭제' 버튼 3개를 늘어놓으면
 * 목록이 버튼 밭이 되어 정작 내용(제목·문서)이 안 읽힌다. 편집 액션은 전부 모달 안으로 넣고
 * 목록은 <b>무엇이 있는지 보는 것</b>에만 집중시킨다.
 *
 * <p>권한 규칙(서버도 동일): 조회는 전 직원, 등록·파일 추가는 내 팀, 수정·삭제는 내가 만든 것.
 * 전체 관리자는 모든 팀. 편집 권한이 없으면 카드가 안 눌린다(읽기 전용).
 */
export default function WorkManualTab({ departments = [], me, isAdmin = false }) {
    const [manuals, setManuals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deptFilter, setDeptFilter] = useState("");
    const [form, setForm] = useState(null);        // null = 모달 닫힘
    const [saving, setSaving] = useState(false);

    const myDeptId = me?.deptId ?? null;   // /employees/me → EmployeeMeResponseDto (id·deptId 평면 구조)
    const canManageDept = (deptId) =>
        isAdmin || (myDeptId != null && String(deptId) === String(myDeptId));
    /** 등록자 정보가 사라진 항목은 같은 팀이 정리할 수 있게 열어둔다(서버와 동일 규칙). */
    const isMine = (ownerId, deptId) =>
        isAdmin || (me?.id != null && (ownerId === me.id || (ownerId == null && canManageDept(deptId))));
    const canCreate = isAdmin || myDeptId != null;

    const load = useCallback(() => {
        setLoading(true);
        workRequestApi.getManuals(deptFilter || undefined)
            .then(list => setManuals(Array.isArray(list) ? list : []))
            .catch(() => setManuals([]))
            .finally(() => setLoading(false));
    }, [deptFilter]);

    useEffect(() => { load(); }, [load]);

    /** 팀별 그룹 — 서버가 이미 부서명 순으로 정렬해 내려준다. */
    const grouped = useMemo(() => {
        const map = new Map();
        manuals.forEach(m => {
            const key = m.deptName || "미지정";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(m);
        });
        return [...map.entries()];
    }, [manuals]);

    const openCreate = () => setForm({ ...EMPTY_FORM, deptId: myDeptId ?? "" });
    const openEdit = (m) => setForm({
        id: m.id, deptId: m.deptId ?? "", title: m.title,
        description: m.description || "", files: [], attachments: m.attachments || [],
    });

    /** 저장 — 본문 저장 후 새로 고른 파일을 이어서 올린다. */
    const save = () => runApiAction({
        request: async () => {
            const payload = {
                deptId: Number(form.deptId),
                title: form.title.trim(),
                description: form.description.trim() || null,
            };
            const saved = form.id ? await workRequestApi.updateManual(form.id, payload)
                                  : await workRequestApi.createManual(payload);
            for (const file of form.files || []) {
                await workRequestApi.uploadManualFile(saved.id, file);
            }
            return saved;
        },
        setLoading: setSaving,
        successMsg: form.id ? "메뉴얼을 수정했습니다." : "메뉴얼을 등록했습니다.",
        onSuccess: () => { setForm(null); load(); },
    });

    const remove = async () => {
        const ok = await confirmDialog({
            title: "업무 메뉴얼 삭제",
            message: `'${form.title}' 과(와) 첨부 파일이 함께 삭제됩니다.\n계속할까요?`,
            confirmLabel: "삭제",
            variant: "danger",
        });
        if (!ok) return;
        runApiAction({
            request: () => workRequestApi.removeManual(form.id),
            setLoading: setSaving,
            successMsg: "메뉴얼을 삭제했습니다.",
            onSuccess: () => { setForm(null); load(); },
        });
    };

    /** 모달 안에서 기존 첨부 삭제 — 지운 결과가 바로 보이게 목록에서도 빼준다. */
    const removeExistingFile = (attachmentId) => runApiAction({
        request: () => workRequestApi.removeManualFile(attachmentId),
        successMsg: "첨부를 삭제했습니다.",
        onSuccess: () => {
            setForm(f => ({ ...f, attachments: (f.attachments || []).filter(a => a.id !== attachmentId) }));
            load();
        },
    });

    const editable = form && (form.id == null || isMine(
        manuals.find(m => m.id === form.id)?.createdById, form.deptId));

    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <TextField
                    select size="small" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
                    sx={{ minWidth: 150 }} SelectProps={{ displayEmpty: true }}
                >
                    <MenuItem value="" sx={{ fontSize: 13 }}>전체 팀</MenuItem>
                    {departments.map(d => (
                        <MenuItem key={d.deptId} value={d.deptId} sx={{ fontSize: 13 }}>{d.deptName}</MenuItem>
                    ))}
                </TextField>
                <Box sx={{ flex: 1 }} />
                {canCreate && (
                    <ActionButton
                        variant="primary" size="small"
                        startIcon={<AddRoundedIcon sx={{ fontSize: 17 }} />}
                        onClick={openCreate}
                        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}   // 없으면 라벨이 두 줄로 깨진다
                    >
                        메뉴얼 등록
                    </ActionButton>
                )}
            </Stack>

            {loading && <LoadingDots />}

            {!loading && manuals.length === 0 && (
                <Box sx={{ p: 4, textAlign: "center", borderRadius: 2, border: `1px dashed ${C.border}`, bgcolor: "#fafbfc" }}>
                    <MenuBookRoundedIcon sx={{ fontSize: 28, color: C.textMuted, mb: 1 }} />
                    <Typography sx={{ fontSize: 13, color: C.textMuted }}>
                        등록된 업무 메뉴얼이 없습니다.
                    </Typography>
                </Box>
            )}

            <Stack spacing={2.5}>
                {grouped.map(([deptName, list]) => (
                    <Box key={deptName}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.textSecondary, mb: 1 }}>
                            {deptName} <Box component="span" sx={{ color: C.textMuted, fontWeight: 400 }}>({list.length})</Box>
                        </Typography>
                        {/* 카드 그리드 — 회의록처럼 한눈에 훑고, 누르면 편집 모달 */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 1.5 }}>
                            {list.map(m => {
                                const canEdit = isMine(m.createdById, m.deptId);
                                return (
                                    <ManualCard
                                        key={m.id}
                                        manual={m}
                                        canEdit={canEdit}
                                        onClick={canEdit ? () => openEdit(m) : undefined}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                ))}
            </Stack>

            <FormModal
                open={Boolean(form)}
                onClose={() => setForm(null)}
                title={form?.id ? "업무 메뉴얼 수정" : "업무 메뉴얼 등록"}
                subtitle="문서를 여러 개 올릴 수 있습니다."
                maxWidth="sm"
                saving={saving}
                saveDisabled={!form?.deptId || !form?.title?.trim()}
                onSave={save}
                footerExtras={form?.id && editable ? (
                    <ActionButton variant="delete" size="small" loading={saving} onClick={remove} />
                ) : null}
            >
                <Stack spacing={2.5}>
                    {/* 관리자는 모든 팀에 등록 가능 → 드롭다운. 일반 직원은 내 소속으로 고정(서버도 거부). */}
                    <FormField label="팀" required={isAdmin}>
                        {isAdmin ? (
                            <TextField
                                select fullWidth size="small"
                                value={form?.deptId ?? ""}
                                onChange={(e) => setForm(f => ({ ...f, deptId: e.target.value }))}
                                helperText="관리자는 모든 팀의 메뉴얼을 등록·수정할 수 있습니다."
                            >
                                {departments.map(d => (
                                    <MenuItem key={d.deptId} value={d.deptId} sx={{ fontSize: 13 }}>
                                        {d.deptName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : (
                            <TextField
                                fullWidth size="small" disabled
                                value={departments.find(d => String(d.deptId) === String(form?.deptId))?.deptName || "-"}
                                helperText="본인 소속 팀의 메뉴얼만 등록·수정할 수 있습니다."
                            />
                        )}
                    </FormField>

                    <FormField label="제목" required>
                        <TextField
                            fullWidth size="small"
                            value={form?.title ?? ""}
                            onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                            inputProps={{ maxLength: 200 }}
                        />
                    </FormField>

                    <FormField label="설명">
                        <TextField
                            fullWidth multiline minRows={3} size="small"
                            value={form?.description ?? ""}
                            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                        />
                    </FormField>

                    {/* 이미 올라간 문서 — 여기서 바로 지울 수 있다(목록 카드에는 버튼을 두지 않는다) */}
                    {form?.attachments?.length > 0 && (
                        <FormField label={`등록된 문서 ${form.attachments.length}개`}>
                            <AttachmentPreviewList
                                attachments={form.attachments}
                                previewFn={workRequestApi.previewManualFile}
                                onDelete={removeExistingFile}
                                canDelete={(a) => isMine(a.uploaderId, form.deptId)}
                                showUploader
                            />
                        </FormField>
                    )}

                    <FormField label="문서 추가">
                        <PendingFileList
                            files={form?.files || []}
                            onChange={(files) => setForm(f => ({ ...f, files }))}
                            label="파일 선택"
                            helper="pdf · jpg · xlsx · ppt 등 (여러 개 가능)"
                        />
                    </FormField>
                </Stack>
            </FormModal>
        </Box>
    );
}

/** 메뉴얼 카드 — 제목·설명·문서 수·등록자. 편집 권한이 있으면 누를 수 있다. */
function ManualCard({ manual, canEdit, onClick }) {
    const count = manual.attachments?.length || 0;
    return (
        <Box
            onClick={onClick}
            sx={{
                p: 1.75, borderRadius: 2, height: "100%",
                border: `1px solid ${C.border}`, bgcolor: "#fff",
                cursor: canEdit ? "pointer" : "default",
                transition: "border-color .15s, box-shadow .15s",
                ...(canEdit ? {
                    "&:hover": { borderColor: C.primaryBorder, boxShadow: "0 2px 10px rgba(15,23,42,0.06)" },
                } : {}),
            }}
        >
            <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 0.75 }}>
                <MenuBookRoundedIcon sx={{ fontSize: 18, color: C.primary, flexShrink: 0, mt: 0.2 }} />
                <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: C.textPrimary, minWidth: 0 }}>
                    {manual.title}
                </Typography>
                <Chip
                    icon={<AttachFileRoundedIcon sx={{ fontSize: 13 }} />}
                    label={count}
                    size="small"
                    sx={{
                        height: 20, fontSize: 11, fontWeight: 700, flexShrink: 0,
                        bgcolor: count ? C.primaryBg : "#f1f5f9",
                        color: count ? C.primaryText : C.textMuted,
                    }}
                />
            </Stack>

            {manual.description && (
                <Typography sx={{
                    fontSize: 12, color: C.textSecondary, mb: 1,
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                }}>
                    {manual.description}
                </Typography>
            )}

            <Typography sx={{ fontSize: 11, color: C.textMuted }}>
                {manual.createdByName}
                {manual.updatedAt ? ` · ${formatDottedDate(manual.updatedAt)}` : ""}
                {canEdit ? " · 눌러서 수정" : ""}
            </Typography>
        </Box>
    );
}
